import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { PromotionsAdminAPI } from "../../api/promotions";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import type {
  Coupon,
  CouponRedemption,
  ReferralRelationship,
  ReferralReward,
  ReferralSettings,
} from "../../types/promotions";

const panelStyle: CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 0,
  background: "#fff",
  padding: 18,
};

const labelStyle: CSSProperties = {
  display: "grid",
  gap: 5,
};

const hintStyle: CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: "#64748b",
};

const defaultSettings: ReferralSettings = {
  singletonKey: "referral",
  enabled: true,
  referrerRewardType: "fixed",
  referrerRewardValue: 10,
  referredRewardEnabled: false,
  referredRewardType: "percentage",
  referredRewardValue: 5,
  minimumReferredOrderAmount: 0,
  rewardExpirationDays: 90,
  allowCouponStacking: false,
  allowMultipleReferralRewardsPerOrder: false,
  requireAuthenticatedReferee: false,
  blockDuplicateByEmail: true,
  blockDuplicateByPhone: true,
  tokenBannerDisplayMode: "conditional",
  tokenBannerCouponCode: "",
  tokenBannerMessage: "",
  emailCaptureEnabled: true,
  emailCaptureCouponCode: "WELCOME10",
  emailCapturePromptDelaySeconds: 6,
  emailCapturePromptSnoozeDays: 14,
  emailCapturePromptDismissDays: 30,
  emailCaptureCouponValidityDays: 14,
};

type CouponFormState = {
  id?: string;
  code: string;
  title: string;
  description: string;
  discountType: "percentage" | "fixed";
  discountValue: number;
  maxUses?: number;
  perCustomerLimit?: number;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  allUsers: boolean;
  autoIssue: boolean;
  conditionFirstOrder: boolean;
  conditionLifetimeSpend: boolean;
  lifetimeSpendMin?: number;
  conditionSpendInWindow: boolean;
  spendInWindowAmount?: number;
  spendInWindowValue?: number;
  spendInWindowUnit: "days" | "weeks" | "months" | "years";
  conditionMinPaidOrders: boolean;
  minPaidOrders?: number;
  affiliateEnabled: boolean;
  affiliateOwnerAuthUid: string;
  affiliateOwnerEmail: string;
  affiliateRewardType: "percentage" | "fixed";
  affiliateRewardValue?: number;
};

type CouponConditionPayload = {
  type: "first_order" | "lifetime_spend" | "spend_in_window" | "order_count";
  minAmount?: number;
  minOrders?: number;
  windowValue?: number;
  windowUnit?: "days" | "weeks" | "months" | "years";
};

type TestReferralRewardFormState = {
  ownerAuthUid: string;
  ownerEmail: string;
  rewardType: "percentage" | "fixed";
  rewardValue: number;
  status: "pending" | "unlocked";
  expiresInDays?: number;
};

const defaultCouponForm: CouponFormState = {
  code: "",
  title: "",
  description: "",
  discountType: "percentage",
  discountValue: 10,
  startsAt: "",
  endsAt: "",
  isActive: true,
  allUsers: true,
  autoIssue: true,
  conditionFirstOrder: false,
  conditionLifetimeSpend: false,
  conditionSpendInWindow: false,
  spendInWindowUnit: "weeks",
  conditionMinPaidOrders: false,
  affiliateEnabled: false,
  affiliateOwnerAuthUid: "",
  affiliateOwnerEmail: "",
  affiliateRewardType: "fixed",
};

const defaultTestReferralRewardForm: TestReferralRewardFormState = {
  ownerAuthUid: "",
  ownerEmail: "",
  rewardType: "fixed",
  rewardValue: 10,
  status: "unlocked",
};

const toDateInput = (value?: string) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const generateCouponCode = () => {
  const bytes = new Uint8Array(6);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    // Older or non-secure browser contexts still need a usable coupon candidate.
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return `SAVE-${Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("").toUpperCase()}`;
};

const money = (value: number | undefined) =>
  `$${Math.max(0, Number(value || 0)).toFixed(2)}`;

function summarizeCouponConditions(coupon: Coupon) {
  const rows = coupon.conditions || [];
  if (!rows.length) return "All users";

  const parts: string[] = [];
  for (const rule of rows) {
    if (rule.type === "first_order") {
      parts.push("First order");
      continue;
    }
    if (rule.type === "lifetime_spend") {
      parts.push(`Lifetime spend >= ${money(rule.minAmount)}`);
      continue;
    }
    if (rule.type === "order_count") {
      parts.push(`Paid orders >= ${Math.max(1, Number(rule.minOrders || 1))}`);
      continue;
    }
    if (rule.type === "spend_in_window") {
      const amount = money(rule.minAmount);
      const windowValue = Math.max(1, Number(rule.windowValue || 1));
      const windowUnit = rule.windowUnit || "weeks";
      parts.push(`Spend >= ${amount} in ${windowValue} ${windowUnit}`);
    }
  }
  return parts.join(" · ");
}

function HeadingWithHelp({
  title,
  help,
}: {
  title: string;
  help: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <h2 style={{ margin: 0, fontSize: 18, color: "#0f172a" }}>{title}</h2>
      <span
        title={help}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 18,
          height: 18,
          borderRadius: 0,
          border: "1px solid #cbd5e1",
          color: "#334155",
          fontSize: 11,
          fontWeight: 700,
          cursor: "help",
          background: "#f8fafc",
        }}
        aria-label={help}
      >
        ?
      </span>
    </div>
  );
}

export default function PromotionsPage() {
  const { isSuperAdmin } = useAdminAuth();

  const [settings, setSettings] = useState<ReferralSettings>(defaultSettings);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [couponForm, setCouponForm] = useState<CouponFormState>(defaultCouponForm);
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponNotice, setCouponNotice] = useState<string | null>(null);
  const [testRewardForm, setTestRewardForm] = useState<TestReferralRewardFormState>(
    defaultTestReferralRewardForm,
  );
  const [issuingTestReward, setIssuingTestReward] = useState(false);
  const [testRewardNotice, setTestRewardNotice] = useState<string | null>(null);
  const [testRewardError, setTestRewardError] = useState<string | null>(null);

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState(true);

  const [referrals, setReferrals] = useState<ReferralRelationship[]>([]);
  const [loadingReferrals, setLoadingReferrals] = useState(true);

  const [rewards, setRewards] = useState<ReferralReward[]>([]);
  const [loadingRewards, setLoadingRewards] = useState(true);

  const [couponRedemptions, setCouponRedemptions] = useState<CouponRedemption[]>([]);
  const [loadingCouponRedemptions, setLoadingCouponRedemptions] = useState(true);

  const editingMode = Boolean(couponForm.id);

  const refreshSettings = async () => {
    setLoadingSettings(true);
    setSettingsError(null);
    try {
      const next = await PromotionsAdminAPI.getReferralSettings();
      setSettings({
        ...defaultSettings,
        ...next,
      });
    } catch (error: any) {
      setSettingsError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load referral settings.",
      );
    } finally {
      setLoadingSettings(false);
    }
  };

  const refreshCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const result = await PromotionsAdminAPI.listCoupons({ limit: 100 });
      setCoupons(result.items || []);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const refreshReferrals = async () => {
    setLoadingReferrals(true);
    try {
      const result = await PromotionsAdminAPI.listReferrals({ limit: 100 });
      setReferrals(result.items || []);
    } finally {
      setLoadingReferrals(false);
    }
  };

  const refreshRewards = async () => {
    setLoadingRewards(true);
    try {
      const result = await PromotionsAdminAPI.listRewards({ limit: 100 });
      setRewards(result.items || []);
    } finally {
      setLoadingRewards(false);
    }
  };

  const refreshCouponRedemptions = async () => {
    setLoadingCouponRedemptions(true);
    try {
      const result = await PromotionsAdminAPI.listCouponRedemptions({ limit: 100 });
      setCouponRedemptions(result.items || []);
    } finally {
      setLoadingCouponRedemptions(false);
    }
  };

  useEffect(() => {
    void refreshSettings();
    void refreshCoupons();
    void refreshReferrals();
    void refreshRewards();
    void refreshCouponRedemptions();
  }, []);

  const applyCouponToForm = (coupon: Coupon) => {
    const firstOrder = coupon.conditions?.some((rule) => rule.type === "first_order");
    const lifetimeSpend = coupon.conditions?.find((rule) => rule.type === "lifetime_spend");
    const spendWindow = coupon.conditions?.find((rule) => rule.type === "spend_in_window");
    const minPaidOrders = coupon.conditions?.find((rule) => rule.type === "order_count");

    setCouponForm({
      id: coupon._id,
      code: coupon.code,
      title: coupon.tokenTitle || coupon.label || "",
      description: coupon.description || "",
      discountType: coupon.discountType,
      discountValue: Number(coupon.discountValue || 0),
      maxUses: coupon.maxUses,
      perCustomerLimit: coupon.perCustomerLimit,
      startsAt: toDateInput(coupon.startsAt),
      endsAt: toDateInput(coupon.endsAt),
      isActive: Boolean(coupon.isActive),
      allUsers: !coupon.conditions?.length,
      autoIssue: Boolean(coupon.autoIssue),
      conditionFirstOrder: Boolean(firstOrder),
      conditionLifetimeSpend: Boolean(lifetimeSpend),
      lifetimeSpendMin: lifetimeSpend?.minAmount ?? undefined,
      conditionSpendInWindow: Boolean(spendWindow),
      spendInWindowAmount: spendWindow?.minAmount ?? undefined,
      spendInWindowValue: spendWindow?.windowValue ?? undefined,
      spendInWindowUnit: spendWindow?.windowUnit ?? "weeks",
      conditionMinPaidOrders: Boolean(minPaidOrders),
      minPaidOrders: minPaidOrders?.minOrders ?? undefined,
      affiliateEnabled: Boolean(coupon.affiliateEnabled),
      affiliateOwnerAuthUid: String(coupon.affiliateOwnerAuthUid || ""),
      affiliateOwnerEmail: String(coupon.affiliateOwnerEmail || ""),
      affiliateRewardType:
        coupon.affiliateRewardType === "percentage" ? "percentage" : "fixed",
      affiliateRewardValue: Number(coupon.affiliateRewardValue || 0) || undefined,
    });
    setCouponError(null);
    setCouponNotice(null);
  };

  const resetCouponForm = () => {
    setCouponForm(defaultCouponForm);
    setCouponError(null);
    setCouponNotice(null);
  };

  const referralStats = useMemo(
    () => ({
      pending: referrals.filter((item) => item.status === "pending").length,
      issued: referrals.filter((item) => item.status === "reward_issued").length,
      invalid: referrals.filter((item) => item.status === "invalid").length,
    }),
    [referrals],
  );

  const rewardStats = useMemo(
    () => ({
      unlocked: rewards.filter((item) => item.status === "unlocked").length,
      redeemed: rewards.filter((item) => item.status === "redeemed").length,
      expired: rewards.filter((item) => item.status === "expired").length,
    }),
    [rewards],
  );

  const affiliateLinkPreview = useMemo(() => {
    const code = String(couponForm.code || "").trim().toUpperCase();
    if (!code) return "";
    if (typeof window === "undefined") return `/products?promo=${encodeURIComponent(code)}`;
    return `${window.location.origin}/products?promo=${encodeURIComponent(code)}`;
  }, [couponForm.code]);

  const onSaveSettings = async () => {
    if (!isSuperAdmin) return;
    setSavingSettings(true);
    setSettingsError(null);
    try {
      const payload: Partial<ReferralSettings> = {
        enabled: settings.enabled,
        referrerRewardType: settings.referrerRewardType,
        referrerRewardValue: Number(settings.referrerRewardValue || 0),
        referredRewardEnabled: settings.referredRewardEnabled,
        referredRewardType: settings.referredRewardType,
        referredRewardValue: Number(settings.referredRewardValue || 0),
        minimumReferredOrderAmount: Number(settings.minimumReferredOrderAmount || 0),
        rewardExpirationDays: settings.rewardExpirationDays || undefined,
        allowCouponStacking: Boolean(settings.allowCouponStacking),
        tokenBannerDisplayMode: settings.tokenBannerDisplayMode,
        tokenBannerCouponCode: String(settings.tokenBannerCouponCode || "").trim(),
        tokenBannerMessage: String(settings.tokenBannerMessage || "").trim(),
        emailCaptureEnabled: Boolean(settings.emailCaptureEnabled),
        emailCaptureCouponCode: String(
          settings.emailCaptureCouponCode || "WELCOME10",
        ).trim(),
        emailCapturePromptDelaySeconds: Number(
          settings.emailCapturePromptDelaySeconds || 0,
        ),
        emailCapturePromptSnoozeDays: Number(
          settings.emailCapturePromptSnoozeDays || 14,
        ),
        emailCapturePromptDismissDays: Number(
          settings.emailCapturePromptDismissDays || 30,
        ),
        emailCaptureCouponValidityDays: Number(
          settings.emailCaptureCouponValidityDays || 14,
        ),
      };
      const next = await PromotionsAdminAPI.updateReferralSettings(payload);
      setSettings({ ...defaultSettings, ...next });
    } catch (error: any) {
      setSettingsError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update referral settings.",
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const onSaveCoupon = async () => {
    setSavingCoupon(true);
    setCouponError(null);
    setCouponNotice(null);
    try {
      const normalizedCode = couponForm.code.trim().toUpperCase();
      if (!normalizedCode) throw new Error("Token code is required.");
      if (Number(couponForm.discountValue || 0) <= 0) {
        throw new Error("Discount value must be greater than 0.");
      }
      if (couponForm.discountType === "percentage" && couponForm.discountValue > 100) {
        throw new Error("Percentage discount cannot exceed 100%.");
      }
      if (!couponForm.allUsers) {
        const hasAnyCondition =
          couponForm.conditionFirstOrder ||
          couponForm.conditionLifetimeSpend ||
          couponForm.conditionSpendInWindow ||
          couponForm.conditionMinPaidOrders;
        if (!hasAnyCondition) {
          throw new Error("Select at least one condition or enable All users.");
        }
      }

      const conditions: CouponConditionPayload[] = [];
      if (!couponForm.allUsers) {
        if (couponForm.conditionFirstOrder) {
          conditions.push({ type: "first_order" });
        }
        if (couponForm.conditionLifetimeSpend) {
          conditions.push({
            type: "lifetime_spend",
            minAmount: Number(couponForm.lifetimeSpendMin || 0),
          });
        }
        if (couponForm.conditionSpendInWindow) {
          conditions.push({
            type: "spend_in_window",
            minAmount: Number(couponForm.spendInWindowAmount || 0),
            windowValue: Number(couponForm.spendInWindowValue || 0),
            windowUnit: couponForm.spendInWindowUnit,
          });
        }
        if (couponForm.conditionMinPaidOrders) {
          conditions.push({
            type: "order_count",
            minOrders: Number(couponForm.minPaidOrders || 0),
          });
        }
      }

      if (couponForm.affiliateEnabled) {
        const ownerUid = String(couponForm.affiliateOwnerAuthUid || "").trim();
        const ownerEmail = String(couponForm.affiliateOwnerEmail || "").trim();
        if (!ownerUid && !ownerEmail) {
          throw new Error("Affiliate owner UID or email is required.");
        }
        if (Number(couponForm.affiliateRewardValue || 0) <= 0) {
          throw new Error("Affiliate reward value must be greater than 0.");
        }
      }

      const payload = {
        code: normalizedCode,
        label: couponForm.title.trim() || undefined,
        description: couponForm.description.trim() || undefined,
        tokenTitle: couponForm.title.trim() || undefined,
        tokenDescription: couponForm.description.trim() || undefined,
        discountType: couponForm.discountType,
        discountValue: Number(couponForm.discountValue || 0),
        minimumOrderAmount: 0,
        maxUses: couponForm.maxUses || 0,
        perCustomerLimit: couponForm.perCustomerLimit || 0,
        startsAt: couponForm.startsAt || "",
        endsAt: couponForm.endsAt || "",
        isActive: couponForm.isActive,
        autoIssue: couponForm.autoIssue,
        ...(couponForm.allUsers ? { requiresEmailCapture: false, applicableProductIds: [], applicableCategories: [] } : {}),
        conditionMode: "all" as const,
        allowWithReferralReward: false,
        allowWithReferredDiscount: true,
        conditions: conditions.length ? conditions : [],
        affiliateEnabled: couponForm.affiliateEnabled,
        affiliateOwnerAuthUid: couponForm.affiliateOwnerAuthUid.trim() || undefined,
        affiliateOwnerEmail: couponForm.affiliateOwnerEmail.trim() || undefined,
        affiliateRewardType: couponForm.affiliateEnabled
          ? couponForm.affiliateRewardType
          : undefined,
        affiliateRewardValue:
          couponForm.affiliateEnabled && Number(couponForm.affiliateRewardValue || 0) > 0
            ? Number(couponForm.affiliateRewardValue)
            : undefined,
      };

      if (couponForm.id) {
        await PromotionsAdminAPI.updateCoupon(couponForm.id, payload);
        setCouponNotice("Token updated.");
      } else {
        await PromotionsAdminAPI.createCoupon(payload);
        setCouponNotice("Token created.");
      }
      if (isSuperAdmin && couponForm.autoIssue && couponForm.allUsers && couponForm.isActive &&
          !couponForm.affiliateEnabled && !couponForm.startsAt && !couponForm.endsAt &&
          !couponForm.maxUses && !couponForm.perCustomerLimit) {
        const updatedSettings = await PromotionsAdminAPI.updateReferralSettings({
          tokenBannerDisplayMode: "all_users",
          tokenBannerCouponCode: normalizedCode,
          tokenBannerMessage: "",
        });
        setSettings(updatedSettings);
        setCouponNotice(`${normalizedCode} is ready and shown to all shoppers.`);
      } else if (isSuperAdmin && !couponForm.autoIssue &&
          String(settings.tokenBannerCouponCode || "").trim().toUpperCase() === normalizedCode) {
        const updatedSettings = await PromotionsAdminAPI.updateReferralSettings({
          tokenBannerDisplayMode: "disabled",
          tokenBannerCouponCode: "",
        });
        setSettings(updatedSettings);
        setCouponNotice(`${normalizedCode} is hidden from the website but remains redeemable.`);
      }
      await refreshCoupons();
      if (!couponForm.id) setCouponForm(defaultCouponForm);
    } catch (error: any) {
      setCouponError(
        error?.response?.data?.message || error?.message || "Failed to save token.",
      );
    } finally {
      setSavingCoupon(false);
    }
  };

  const onDeleteCoupon = async (couponId: string) => {
    setSavingCoupon(true);
    setCouponError(null);
    setCouponNotice(null);
    try {
      const result = await PromotionsAdminAPI.deleteCoupon(couponId);
      setCouponNotice(result.message || "Token removed.");
      await refreshCoupons();
      if (couponForm.id === couponId) resetCouponForm();
    } catch (error: any) {
      setCouponError(
        error?.response?.data?.message || error?.message || "Failed to delete token.",
      );
    } finally {
      setSavingCoupon(false);
    }
  };

  const copyAffiliateLink = async () => {
    if (!affiliateLinkPreview) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(affiliateLinkPreview);
        setCouponNotice("Affiliate link copied.");
        return;
      }
    } catch {
      // fallback below
    }
    const textArea = document.createElement("textarea");
    textArea.value = affiliateLinkPreview;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    setCouponNotice("Affiliate link copied.");
  };

  const issueTestReferralToken = async () => {
    setIssuingTestReward(true);
    setTestRewardError(null);
    setTestRewardNotice(null);
    try {
      const ownerAuthUid = String(testRewardForm.ownerAuthUid || "").trim();
      if (!ownerAuthUid) {
        throw new Error("Creator/customer Firebase UID is required.");
      }
      const rewardValue = Number(testRewardForm.rewardValue || 0);
      if (!Number.isFinite(rewardValue) || rewardValue <= 0) {
        throw new Error("Reward value must be greater than 0.");
      }
      const reward = await PromotionsAdminAPI.createTestReferralRewardToken({
        ownerAuthUid,
        ownerEmail: String(testRewardForm.ownerEmail || "").trim() || undefined,
        rewardType: testRewardForm.rewardType,
        rewardValue,
        status: testRewardForm.status,
        expiresInDays:
          Number(testRewardForm.expiresInDays || 0) > 0
            ? Number(testRewardForm.expiresInDays)
            : undefined,
      });
      await refreshRewards();
      setTestRewardNotice(
        `Test token created: ${String(reward.rewardCode || "N/A")} (owner UID: ${reward.ownerAuthUid})`,
      );
    } catch (error: any) {
      setTestRewardError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to issue test referral token.",
      );
    } finally {
      setIssuingTestReward(false);
    }
  };

  return (
    <div
      className="promotions-page-shell"
      style={{ display: "grid", gap: 18, maxWidth: 1280, margin: "0 auto", width: "100%" }}
    >
      <style>{`
        .promotions-page-shell input:not([type="checkbox"]),
        .promotions-page-shell select,
        .promotions-page-shell textarea {
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 0;
          min-height: 38px;
          padding: 8px 10px;
          background: #fff;
          color: #0f172a;
          box-sizing: border-box;
        }
        .promotions-page-shell textarea {
          min-height: 74px;
          resize: vertical;
        }
        .promotions-page-shell input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: #0f172a;
        }
        .promotions-page-shell table th {
          font-weight: 700;
          white-space: nowrap;
        }
        .promotions-page-shell table td {
          vertical-align: top;
        }
      `}</style>
      <section style={panelStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h1 style={{ margin: 0, fontSize: 22, color: "#0f172a" }}>
                Referrals & Coupons
              </h1>
              <span
                title="Control referral rewards, coupon tokens, and affiliate earning rules from one page."
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  borderRadius: 0,
                  border: "1px solid #cbd5e1",
                  color: "#334155",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "help",
                  background: "#f8fafc",
                }}
                aria-label="Control referral rewards, coupon tokens, and affiliate earning rules from one page."
              >
                ?
              </span>
            </div>
            <p style={{ margin: "6px 0 0", color: "#475569", fontSize: 13 }}>
              Simplified setup for referral rewards, promo tokens, and creator affiliate links.
            </p>
          </div>
          {isSuperAdmin ? (
            <button
              type="button"
              onClick={() => void onSaveSettings()}
              disabled={loadingSettings || savingSettings}
              style={{
                border: "1px solid #0f172a",
                background: "#0f172a",
                color: "#fff",
                borderRadius: 0,
                padding: "8px 12px",
                fontWeight: 700,
                cursor: loadingSettings || savingSettings ? "default" : "pointer",
                opacity: loadingSettings || savingSettings ? 0.7 : 1,
              }}
            >
              {savingSettings ? "Saving..." : "Save Referral Settings"}
            </button>
          ) : null}
        </div>

        {settingsError ? (
          <p style={{ marginTop: 10, color: "#b91c1c", fontSize: 13 }}>{settingsError}</p>
        ) : null}

        {loadingSettings ? (
          <p style={{ marginTop: 12, color: "#475569", fontSize: 13 }}>Loading settings...</p>
        ) : (
          <div
            style={{
              marginTop: 14,
              display: "grid",
              gap: 10,
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            }}
          >
            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>Program enabled</span>
              <input
                type="checkbox"
                checked={settings.enabled}
                disabled={!isSuperAdmin}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, enabled: e.target.checked }))
                }
              />
            </label>
            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>Referrer reward type</span>
              <select
                value={settings.referrerRewardType}
                disabled={!isSuperAdmin}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    referrerRewardType: e.target.value as "percentage" | "fixed",
                  }))
                }
              >
                <option value="fixed">Fixed amount ($)</option>
                <option value="percentage">Percentage (%)</option>
              </select>
            </label>
            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>Referrer reward value</span>
              <input
                type="number"
                min={0}
                disabled={!isSuperAdmin}
                value={settings.referrerRewardValue}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    referrerRewardValue: Number(e.target.value || 0),
                  }))
                }
              />
            </label>

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>
                Referred customer gets reward
              </span>
              <input
                type="checkbox"
                checked={settings.referredRewardEnabled}
                disabled={!isSuperAdmin}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    referredRewardEnabled: e.target.checked,
                  }))
                }
              />
            </label>

            {settings.referredRewardEnabled ? (
              <>
                <label style={labelStyle}>
                  <span style={{ fontSize: 12, color: "#475569" }}>
                    Referred reward type
                  </span>
                  <select
                    value={settings.referredRewardType}
                    disabled={!isSuperAdmin}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        referredRewardType: e.target.value as "percentage" | "fixed",
                      }))
                    }
                  >
                    <option value="fixed">Fixed amount ($)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </label>
                <label style={labelStyle}>
                  <span style={{ fontSize: 12, color: "#475569" }}>
                    Referred reward value
                  </span>
                  <input
                    type="number"
                    min={0}
                    disabled={!isSuperAdmin}
                    value={settings.referredRewardValue}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        referredRewardValue: Number(e.target.value || 0),
                      }))
                    }
                  />
                </label>
              </>
            ) : null}

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>Min referred order amount</span>
              <input
                type="number"
                min={0}
                disabled={!isSuperAdmin}
                value={settings.minimumReferredOrderAmount}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    minimumReferredOrderAmount: Number(e.target.value || 0),
                  }))
                }
              />
            </label>

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>Reward expiration (days)</span>
              <input
                type="number"
                min={1}
                disabled={!isSuperAdmin}
                value={settings.rewardExpirationDays || ""}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    rewardExpirationDays: Number(e.target.value || 0) || undefined,
                  }))
                }
              />
            </label>

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>
                Allow multiple promo discounts
              </span>
              <input
                type="checkbox"
                checked={settings.allowCouponStacking}
                disabled={!isSuperAdmin}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    allowCouponStacking: e.target.checked,
                  }))
                }
              />
              <span style={hintStyle}>
                Off by default. When off, customers cannot combine coupon and referral
                discounts.
              </span>
            </label>

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>
                Email offer prompt enabled
              </span>
              <input
                type="checkbox"
                checked={settings.emailCaptureEnabled}
                disabled={!isSuperAdmin}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    emailCaptureEnabled: e.target.checked,
                  }))
                }
              />
            </label>

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>
                Email offer coupon code
              </span>
              <input
                type="text"
                placeholder="WELCOME10"
                disabled={!isSuperAdmin}
                value={settings.emailCaptureCouponCode || ""}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    emailCaptureCouponCode: e.target.value.toUpperCase(),
                  }))
                }
              />
              <span style={hintStyle}>
                The backend keeps this as an active 10% first-order coupon.
              </span>
            </label>

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>
                Prompt delay (seconds)
              </span>
              <input
                type="number"
                min={0}
                disabled={!isSuperAdmin}
                value={settings.emailCapturePromptDelaySeconds}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    emailCapturePromptDelaySeconds: Number(e.target.value || 0),
                  }))
                }
              />
            </label>

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>
                Snooze after dismiss (days)
              </span>
              <input
                type="number"
                min={1}
                disabled={!isSuperAdmin}
                value={settings.emailCapturePromptSnoozeDays}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    emailCapturePromptSnoozeDays: Number(e.target.value || 14),
                  }))
                }
              />
            </label>

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>
                Token validity (days)
              </span>
              <input
                type="number"
                min={1}
                disabled={!isSuperAdmin}
                value={settings.emailCaptureCouponValidityDays}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    emailCaptureCouponValidityDays: Number(e.target.value || 14),
                  }))
                }
              />
            </label>

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>
                Snooze after close (days)
              </span>
              <input
                type="number"
                min={1}
                disabled={!isSuperAdmin}
                value={settings.emailCapturePromptDismissDays}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    emailCapturePromptDismissDays: Number(e.target.value || 30),
                  }))
                }
              />
            </label>

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>Offer banner mode</span>
              <select
                value={settings.tokenBannerDisplayMode || "conditional"}
                disabled={!isSuperAdmin}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    tokenBannerDisplayMode:
                      e.target.value === "all_users" ||
                      e.target.value === "disabled" ||
                      e.target.value === "conditional"
                        ? e.target.value
                        : "conditional",
                  }))
                }
              >
                <option value="conditional">Conditional token logic</option>
                <option value="all_users">Show configured token for all users</option>
                <option value="disabled">Do not show token banner</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>Banner token code</span>
              <input
                type="text"
                placeholder="BPVTD10"
                disabled={!isSuperAdmin}
                value={settings.tokenBannerCouponCode || ""}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    tokenBannerCouponCode: e.target.value.toUpperCase(),
                  }))
                }
              />
            </label>

            <label style={{ ...labelStyle, gridColumn: "1 / -1" }}>
              <span style={{ fontSize: 12, color: "#475569" }}>Banner message</span>
              <input
                type="text"
                placeholder="Use BPVTD10 for this Valentine's Day discount."
                disabled={!isSuperAdmin}
                value={settings.tokenBannerMessage || ""}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    tokenBannerMessage: e.target.value,
                  }))
                }
              />
              <span style={hintStyle}>
                This message appears in the customer token banner when active.
              </span>
            </label>
          </div>
        )}
      </section>

      <section style={panelStyle}>
        <HeadingWithHelp
          title="Coupon & Affiliate Tokens"
          help="Coupons are customer-facing promo tokens. Affiliate tokens also credit a creator wallet when their audience uses the token."
        />
        <p style={{ margin: "6px 0 0", color: "#475569", fontSize: 13 }}>
          Create one token at a time. Keep the form minimal and condition-driven.
        </p>

        <button type="button" style={{ marginTop: 12 }} onClick={() => setCouponForm(prev => ({
          ...prev, allUsers: true, startsAt: "", endsAt: "", maxUses: undefined,
          perCustomerLimit: undefined, affiliateEnabled: false,
        }))}>Make shareable for all users · remove expiry and limits</button>
        <p style={{ fontSize: 12, color: "#475569" }}>Changes take effect when you save. Disabled tokens stay disabled.</p>
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gap: 8,
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Token code</span>
            <button type="button" onClick={() => setCouponForm(prev => ({ ...prev, code: generateCouponCode() }))}>Generate code</button>
            <input
              value={couponForm.code}
              placeholder="BPVTD10"
              onChange={(e) =>
                setCouponForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
              }
            />
          </label>

          <label style={labelStyle}>
            <span><input type="checkbox" checked={couponForm.autoIssue} onChange={e => setCouponForm(prev => ({ ...prev, autoIssue: e.target.checked }))} /> Show this code on the website</span>
            <small>A super admin publishes an unrestricted active token in the global banner. Other eligible tokens may appear as automatic offers. Uncheck to hide it; shared codes still redeem.</small>
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Title</span>
            <input
              value={couponForm.title}
              placeholder="Valentine Special"
              onChange={(e) =>
                setCouponForm((prev) => ({ ...prev, title: e.target.value }))
              }
            />
          </label>

          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Discount type</span>
            <select
              value={couponForm.discountType}
              onChange={(e) =>
                setCouponForm((prev) => ({
                  ...prev,
                  discountType: e.target.value === "fixed" ? "fixed" : "percentage",
                }))
              }
            >
              <option value="percentage">% off</option>
              <option value="fixed">$ off</option>
            </select>
          </label>

          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Discount value</span>
            <input
              type="number"
              min={0}
              value={couponForm.discountValue}
              onChange={(e) =>
                setCouponForm((prev) => ({
                  ...prev,
                  discountValue: Number(e.target.value || 0),
                }))
              }
            />
          </label>

          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Max users</span>
            <input
              type="number"
              min={0}
              value={couponForm.maxUses ?? ""}
              onChange={(e) =>
                setCouponForm((prev) => ({
                  ...prev,
                  maxUses: Number(e.target.value || 0) || undefined,
                }))
              }
            />
          </label>

          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Per-user limit</span>
            <input
              type="number"
              min={0}
              value={couponForm.perCustomerLimit ?? ""}
              onChange={(e) =>
                setCouponForm((prev) => ({
                  ...prev,
                  perCustomerLimit: Number(e.target.value || 0) || undefined,
                }))
              }
            />
            <span style={hintStyle}>
              How many times one customer account can use this same token.
            </span>
          </label>

          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Start date</span>
            <input
              type="date"
              value={couponForm.startsAt}
              onChange={(e) =>
                setCouponForm((prev) => ({ ...prev, startsAt: e.target.value }))
              }
            />
          </label>

          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>End date</span>
            <input
              type="date"
              value={couponForm.endsAt}
              onChange={(e) =>
                setCouponForm((prev) => ({ ...prev, endsAt: e.target.value }))
              }
            />
          </label>

          <label
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              paddingTop: 24,
              fontSize: 13,
              color: "#334155",
            }}
          >
            <input
              type="checkbox"
              checked={couponForm.isActive}
              onChange={(e) =>
                setCouponForm((prev) => ({ ...prev, isActive: e.target.checked }))
              }
            />
            Active token
          </label>

          <label style={{ ...labelStyle, gridColumn: "1 / -1" }}>
            <span style={{ fontSize: 12, color: "#475569" }}>Description</span>
            <textarea
              style={{ minHeight: 70 }}
              placeholder="What this token is for."
              value={couponForm.description}
              onChange={(e) =>
                setCouponForm((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </label>
        </div>

        <div
          style={{
            marginTop: 12,
            border: "1px solid #e2e8f0",
            borderRadius: 0,
            padding: 12,
            background: "#f8fafc",
          }}
        >
          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>Conditions</div>
          <label
            style={{
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "#334155",
            }}
          >
            <input
              type="checkbox"
              checked={couponForm.allUsers}
              onChange={(e) =>
                setCouponForm((prev) => ({ ...prev, allUsers: e.target.checked }))
              }
            />
            All users (no qualification required)
          </label>

          {!couponForm.allUsers ? (
            <div
              style={{
                marginTop: 10,
                display: "grid",
                gap: 10,
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  color: "#334155",
                }}
              >
                <input
                  type="checkbox"
                  checked={couponForm.conditionFirstOrder}
                  onChange={(e) =>
                    setCouponForm((prev) => ({
                      ...prev,
                      conditionFirstOrder: e.target.checked,
                    }))
                  }
                />
                First-time customer
              </label>

              <div style={{ display: "grid", gap: 6 }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    color: "#334155",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={couponForm.conditionLifetimeSpend}
                    onChange={(e) =>
                      setCouponForm((prev) => ({
                        ...prev,
                        conditionLifetimeSpend: e.target.checked,
                      }))
                    }
                  />
                  Lifetime spend threshold
                </label>
                {couponForm.conditionLifetimeSpend ? (
                  <input
                    type="number"
                    min={0}
                    placeholder="Spend at least $"
                    value={couponForm.lifetimeSpendMin ?? ""}
                    onChange={(e) =>
                      setCouponForm((prev) => ({
                        ...prev,
                        lifetimeSpendMin: Number(e.target.value || 0) || undefined,
                      }))
                    }
                  />
                ) : null}
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    color: "#334155",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={couponForm.conditionSpendInWindow}
                    onChange={(e) =>
                      setCouponForm((prev) => ({
                        ...prev,
                        conditionSpendInWindow: e.target.checked,
                      }))
                    }
                  />
                  Spend in time window
                </label>
                {couponForm.conditionSpendInWindow ? (
                  <div style={{ display: "grid", gap: 6 }}>
                    <input
                      type="number"
                      min={0}
                      placeholder="Spend at least $"
                      value={couponForm.spendInWindowAmount ?? ""}
                      onChange={(e) =>
                        setCouponForm((prev) => ({
                          ...prev,
                          spendInWindowAmount: Number(e.target.value || 0) || undefined,
                        }))
                      }
                    />
                    <div
                      style={{
                        display: "grid",
                        gap: 6,
                        gridTemplateColumns: "1fr 1fr",
                      }}
                    >
                      <input
                        type="number"
                        min={1}
                        placeholder="Window size"
                        value={couponForm.spendInWindowValue ?? ""}
                        onChange={(e) =>
                          setCouponForm((prev) => ({
                            ...prev,
                            spendInWindowValue: Number(e.target.value || 0) || undefined,
                          }))
                        }
                      />
                      <select
                        value={couponForm.spendInWindowUnit}
                        onChange={(e) =>
                          setCouponForm((prev) => ({
                            ...prev,
                            spendInWindowUnit:
                              e.target.value === "days" ||
                              e.target.value === "weeks" ||
                              e.target.value === "months" ||
                              e.target.value === "years"
                                ? e.target.value
                                : "weeks",
                          }))
                        }
                      >
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                        <option value="years">Years</option>
                      </select>
                    </div>
                  </div>
                ) : null}
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    color: "#334155",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={couponForm.conditionMinPaidOrders}
                    onChange={(e) =>
                      setCouponForm((prev) => ({
                        ...prev,
                        conditionMinPaidOrders: e.target.checked,
                      }))
                    }
                  />
                  Minimum paid orders
                </label>
                {couponForm.conditionMinPaidOrders ? (
                  <input
                    type="number"
                    min={1}
                    placeholder="At least N orders"
                    value={couponForm.minPaidOrders ?? ""}
                    onChange={(e) =>
                      setCouponForm((prev) => ({
                        ...prev,
                        minPaidOrders: Number(e.target.value || 0) || undefined,
                      }))
                    }
                  />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <div
          style={{
            marginTop: 12,
            border: "1px solid #e2e8f0",
            borderRadius: 0,
            padding: 12,
            background: "#f8fafc",
          }}
        >
          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>
            Affiliate Link (Creator Rewards)
          </div>
          <p style={{ margin: "4px 0 8px", color: "#64748b", fontSize: 12 }}>
            If enabled, purchases using this token add credit to the creator wallet. Credits
            accumulate into one redeemable token.
          </p>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "#334155",
            }}
          >
            <input
              type="checkbox"
              checked={couponForm.affiliateEnabled}
              onChange={(e) =>
                setCouponForm((prev) => ({ ...prev, affiliateEnabled: e.target.checked }))
              }
            />
            Enable affiliate earning for this token
          </label>

          {couponForm.affiliateEnabled ? (
            <>
              <div
                style={{
                  marginTop: 10,
                  display: "grid",
                  gap: 8,
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                }}
              >
              <label style={labelStyle}>
                <span style={{ fontSize: 12, color: "#475569" }}>Creator account UID</span>
                <input
                  placeholder="Firebase auth UID"
                  value={couponForm.affiliateOwnerAuthUid}
                  onChange={(e) =>
                    setCouponForm((prev) => ({
                      ...prev,
                      affiliateOwnerAuthUid: e.target.value,
                    }))
                  }
                />
                <span style={hintStyle}>
                  Use the Firebase Authentication UID of the creator account that should earn
                  rewards.
                </span>
              </label>
              <label style={labelStyle}>
                <span style={{ fontSize: 12, color: "#475569" }}>Creator email (optional)</span>
                <input
                  type="email"
                  placeholder="creator@email.com"
                  value={couponForm.affiliateOwnerEmail}
                  onChange={(e) =>
                    setCouponForm((prev) => ({
                      ...prev,
                      affiliateOwnerEmail: e.target.value,
                    }))
                  }
                />
                <span style={hintStyle}>
                  Optional backup identifier. UID is still the primary key for affiliate credit.
                </span>
              </label>
              <label style={labelStyle}>
                <span style={{ fontSize: 12, color: "#475569" }}>Creator reward type</span>
                <select
                  value={couponForm.affiliateRewardType}
                  onChange={(e) =>
                    setCouponForm((prev) => ({
                      ...prev,
                      affiliateRewardType:
                        e.target.value === "percentage" ? "percentage" : "fixed",
                    }))
                  }
                >
                  <option value="fixed">$ fixed</option>
                  <option value="percentage">% of referred subtotal</option>
                </select>
              </label>
              <label style={labelStyle}>
                <span style={{ fontSize: 12, color: "#475569" }}>Creator reward value</span>
                <input
                  type="number"
                  min={0}
                  value={couponForm.affiliateRewardValue ?? ""}
                  onChange={(e) =>
                    setCouponForm((prev) => ({
                      ...prev,
                      affiliateRewardValue: Number(e.target.value || 0) || undefined,
                    }))
                  }
                />
              </label>
              </div>
              <div
                style={{
                  marginTop: 10,
                  border: "1px solid #dbeafe",
                  borderRadius: 0,
                  padding: "8px 10px",
                  background: "#eff6ff",
                  display: "grid",
                  gap: 6,
                }}
              >
              <div style={{ fontSize: 12, color: "#1e3a8a", fontWeight: 700 }}>
                Affiliate link preview
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#1e293b",
                  wordBreak: "break-all",
                }}
              >
                {affiliateLinkPreview || "Enter a token code to generate the affiliate link."}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => void copyAffiliateLink()}
                  disabled={!affiliateLinkPreview}
                  style={{
                    border: "1px solid #93c5fd",
                    background: "#fff",
                    color: "#1d4ed8",
                    borderRadius: 0,
                    padding: "6px 10px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: affiliateLinkPreview ? "pointer" : "not-allowed",
                    opacity: affiliateLinkPreview ? 1 : 0.6,
                  }}
                >
                  Copy affiliate link
                </button>
              </div>
              </div>
            </>
          ) : null}
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => void onSaveCoupon()}
            disabled={savingCoupon}
            style={{
              border: "1px solid #0f172a",
              background: "#0f172a",
              color: "#fff",
              borderRadius: 0,
              padding: "8px 12px",
              fontWeight: 700,
            }}
          >
            {savingCoupon ? "Saving..." : editingMode ? "Update Token" : "Create Token"}
          </button>
          <button
            type="button"
            onClick={resetCouponForm}
            disabled={savingCoupon}
            style={{
              border: "1px solid #cbd5e1",
              background: "#fff",
              color: "#0f172a",
              borderRadius: 0,
              padding: "8px 12px",
              fontWeight: 700,
            }}
          >
            Reset
          </button>
        </div>

        {couponError ? (
          <p style={{ marginTop: 8, color: "#b91c1c", fontSize: 13 }}>{couponError}</p>
        ) : null}
        {couponNotice ? (
          <p style={{ marginTop: 8, color: "#047857", fontSize: 13 }}>{couponNotice}</p>
        ) : null}

        <div
          style={{
            marginTop: 14,
            overflowX: "auto",
            border: "1px solid #e2e8f0",
            borderRadius: 0,
            background: "#fff",
          }}
        >
          {loadingCoupons ? (
            <p style={{ fontSize: 13, color: "#475569", padding: 12 }}>Loading tokens...</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#334155" }}>
                  <th
                    style={{ padding: 8, textAlign: "left" }}
                    title="Coupon code and display title shown to shoppers."
                  >
                    Token
                  </th>
                  <th
                    style={{ padding: 8, textAlign: "left" }}
                    title="Discount rule applied when token is valid."
                  >
                    Discount
                  </th>
                  <th style={{ padding: 8, textAlign: "left" }}>Conditions</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Usage</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon._id} style={{ borderTop: "1px solid #e2e8f0" }}>
                    <td style={{ padding: 8 }}>
                      <div style={{ fontWeight: 700 }}>{coupon.code}</div>
                      <div style={{ color: "#64748b", fontSize: 12 }}>
                        {coupon.label || coupon.description || "-"}
                      </div>
                      {coupon.affiliateEnabled ? (
                        <div style={{ marginTop: 4, color: "#0369a1", fontSize: 11 }}>
                          Affiliate token
                        </div>
                      ) : null}
                    </td>
                    <td style={{ padding: 8 }}>
                      {coupon.discountType === "percentage"
                        ? `${coupon.discountValue}% off`
                        : `${money(coupon.discountValue)} off`}
                      <div style={{ color: "#64748b", fontSize: 12 }}>
                        {coupon.isActive ? "Active" : "Inactive"}
                      </div>
                    </td>
                    <td style={{ padding: 8 }}>{summarizeCouponConditions(coupon)}</td>
                    <td style={{ padding: 8 }}>
                      {coupon.usedCount}
                      {coupon.maxUses ? ` / ${coupon.maxUses}` : ""}
                    </td>
                    <td style={{ padding: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => applyCouponToForm(coupon)}
                        style={{
                          border: "1px solid #cbd5e1",
                          background: "#fff",
                          color: "#0f172a",
                          borderRadius: 0,
                          padding: "5px 10px",
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => void onDeleteCoupon(coupon._id)}
                        disabled={savingCoupon}
                        style={{
                          border: "1px solid #fecaca",
                          background: "#fff1f2",
                          color: "#b91c1c",
                          borderRadius: 0,
                          padding: "5px 10px",
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section style={panelStyle}>
        <HeadingWithHelp
          title="Referral Records"
          help="Every row links a referrer and referred customer to the qualifying order and current referral status."
        />
        <p style={{ marginTop: 4, color: "#64748b", fontSize: 13 }}>
          Pending: {referralStats.pending} · Reward issued: {referralStats.issued} ·
          Invalid: {referralStats.invalid}
        </p>
        <div
          style={{
            marginTop: 12,
            overflowX: "auto",
            border: "1px solid #e2e8f0",
            borderRadius: 0,
            background: "#fff",
          }}
        >
          {loadingReferrals ? (
            <p style={{ fontSize: 13, color: "#475569", padding: 12 }}>
              Loading referral records...
            </p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#334155" }}>
                  <th style={{ padding: 8, textAlign: "left" }}>Code</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Referrer</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Referred</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Order</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((item) => (
                  <tr key={item._id} style={{ borderTop: "1px solid #e2e8f0" }}>
                    <td style={{ padding: 8 }}>{item.referralCode}</td>
                    <td style={{ padding: 8 }}>{item.referrerEmail || item.referrerAuthUid}</td>
                    <td style={{ padding: 8 }}>
                      {item.referredEmail || item.referredAuthUid || item.referredGuestId || "-"}
                    </td>
                    <td style={{ padding: 8 }}>
                      {item.referredOrderNumber || "-"}
                      {item.referredOrderSubtotal != null
                        ? ` (${money(item.referredOrderSubtotal)})`
                        : ""}
                    </td>
                    <td style={{ padding: 8 }}>{item.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section style={panelStyle}>
        <HeadingWithHelp
          title="Reward Ledger"
          help="Tracks earned referral rewards and whether each reward is unlocked, redeemed, or expired."
        />
        <p style={{ marginTop: 4, color: "#64748b", fontSize: 13 }}>
          Unlocked: {rewardStats.unlocked} · Redeemed: {rewardStats.redeemed} · Expired:{" "}
          {rewardStats.expired}
        </p>
        <div
          style={{
            marginTop: 12,
            overflowX: "auto",
            border: "1px solid #e2e8f0",
            borderRadius: 0,
            background: "#fff",
          }}
        >
          {loadingRewards ? (
            <p style={{ fontSize: 13, color: "#475569", padding: 12 }}>Loading rewards...</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#334155" }}>
                  <th style={{ padding: 8, textAlign: "left" }}>Owner</th>
                  <th
                    style={{ padding: 8, textAlign: "left" }}
                    title="Reward token/code issued to the owner."
                  >
                    Token
                  </th>
                  <th
                    style={{ padding: 8, textAlign: "left" }}
                    title="Reward amount configured for this reward entry."
                  >
                    Reward
                  </th>
                  <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rewards.map((item) => (
                  <tr key={item._id} style={{ borderTop: "1px solid #e2e8f0" }}>
                    <td style={{ padding: 8 }}>{item.ownerEmail || item.ownerAuthUid}</td>
                    <td style={{ padding: 8 }}>{item.rewardCode || "-"}</td>
                    <td style={{ padding: 8 }}>
                      {item.rewardType === "percentage"
                        ? `${item.rewardValue}%`
                        : money(item.rewardValue)}
                    </td>
                    <td style={{ padding: 8 }}>{item.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section style={panelStyle}>
        <HeadingWithHelp
          title="Test Referral Tokens"
          help="Generate a valid referral reward token for a specific Firebase UID so QA can test redemption flow quickly."
        />
        <p style={{ marginTop: 4, color: "#64748b", fontSize: 13 }}>
          Use this only for QA/staging. Token redemption works only when checkout is signed in
          with the same owner UID.
        </p>
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gap: 10,
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Owner Firebase UID</span>
            <input
              placeholder="auth UID that will redeem this token"
              value={testRewardForm.ownerAuthUid}
              onChange={(e) =>
                setTestRewardForm((prev) => ({ ...prev, ownerAuthUid: e.target.value }))
              }
            />
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Owner email (optional)</span>
            <input
              type="email"
              placeholder="user@email.com"
              value={testRewardForm.ownerEmail}
              onChange={(e) =>
                setTestRewardForm((prev) => ({ ...prev, ownerEmail: e.target.value }))
              }
            />
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Reward type</span>
            <select
              value={testRewardForm.rewardType}
              onChange={(e) =>
                setTestRewardForm((prev) => ({
                  ...prev,
                  rewardType: e.target.value === "percentage" ? "percentage" : "fixed",
                }))
              }
            >
              <option value="fixed">$ fixed</option>
              <option value="percentage">% percent</option>
            </select>
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Reward value</span>
            <input
              type="number"
              min={1}
              value={testRewardForm.rewardValue}
              onChange={(e) =>
                setTestRewardForm((prev) => ({
                  ...prev,
                  rewardValue: Number(e.target.value || 0) || 0,
                }))
              }
            />
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Status</span>
            <select
              value={testRewardForm.status}
              onChange={(e) =>
                setTestRewardForm((prev) => ({
                  ...prev,
                  status: e.target.value === "pending" ? "pending" : "unlocked",
                }))
              }
            >
              <option value="unlocked">Unlocked (ready to redeem)</option>
              <option value="pending">Pending</option>
            </select>
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Expires in days (optional)</span>
            <input
              type="number"
              min={1}
              value={testRewardForm.expiresInDays ?? ""}
              onChange={(e) =>
                setTestRewardForm((prev) => ({
                  ...prev,
                  expiresInDays: Number(e.target.value || 0) || undefined,
                }))
              }
            />
          </label>
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => void issueTestReferralToken()}
            disabled={issuingTestReward}
            style={{
              border: "1px solid #0f172a",
              background: "#0f172a",
              color: "#fff",
              borderRadius: 0,
              padding: "8px 12px",
              fontWeight: 700,
            }}
          >
            {issuingTestReward ? "Issuing..." : "Generate Test Token"}
          </button>
          <button
            type="button"
            onClick={() => {
              setTestRewardForm(defaultTestReferralRewardForm);
              setTestRewardError(null);
              setTestRewardNotice(null);
            }}
            disabled={issuingTestReward}
            style={{
              border: "1px solid #cbd5e1",
              background: "#fff",
              color: "#0f172a",
              borderRadius: 0,
              padding: "8px 12px",
              fontWeight: 700,
            }}
          >
            Reset
          </button>
        </div>
        {testRewardError ? (
          <p style={{ marginTop: 8, color: "#b91c1c", fontSize: 13 }}>{testRewardError}</p>
        ) : null}
        {testRewardNotice ? (
          <p style={{ marginTop: 8, color: "#047857", fontSize: 13 }}>{testRewardNotice}</p>
        ) : null}
      </section>

      <section style={panelStyle}>
        <HeadingWithHelp
          title="Coupon Redemption History"
          help="Audit table for token usage across orders, customers, discount amounts, and settlement status."
        />
        <div
          style={{
            marginTop: 12,
            overflowX: "auto",
            border: "1px solid #e2e8f0",
            borderRadius: 0,
            background: "#fff",
          }}
        >
          {loadingCouponRedemptions ? (
            <p style={{ fontSize: 13, color: "#475569", padding: 12 }}>
              Loading redemption history...
            </p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#334155" }}>
                  <th
                    style={{ padding: 8, textAlign: "left" }}
                    title="Coupon code redeemed on the order."
                  >
                    Coupon
                  </th>
                  <th style={{ padding: 8, textAlign: "left" }}>Order</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Customer</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Discount</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {couponRedemptions.map((item) => (
                  <tr key={item._id} style={{ borderTop: "1px solid #e2e8f0" }}>
                    <td style={{ padding: 8 }}>{item.couponCode}</td>
                    <td style={{ padding: 8 }}>{item.orderNumber || item.orderId}</td>
                    <td style={{ padding: 8 }}>
                      {item.customerEmail || item.customerAuthUid || item.customerGuestId || "-"}
                    </td>
                    <td style={{ padding: 8 }}>{money(item.discountAmount)}</td>
                    <td style={{ padding: 8 }}>{item.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
